// Shared material-pick resolution. A scene instance stores its finishes as
// surfaceKey → materialId; this turns that into display-ready
// { surfaceLabel, materialName } pairs using the product-detail cache. Used by
// both the cart's product-materials block (ProductTableBlock) and the PDF
// payload builder (pdfRender) so the cart and PDF list identical finishes.

import type { RowMaterial } from "./blocks/RowContext";
import type { SceneProductDetailResponse } from "./types";

export type { RowMaterial };

/** Resolve a product's finishes to display names. Walks the detail's surfaces in
 *  declared order (stable, not picks-map order). Only user-choosable surfaces are
 *  included — `locked` surfaces are fixed finishes hidden from the picker, so they
 *  aren't part of "the materials the customer chose". For each choosable surface
 *  it uses the user's pick when present, otherwise falls back to the surface's
 *  default finish — so a product shows its finishes even before the user changes
 *  any. Surfaces with neither a (resolvable) pick nor a default are skipped.
 *  Returns [] when the detail isn't cached yet or the product id is empty. */
export function resolveRowMaterials(
  productId: string,
  picks: Record<string, string>,
  detailCache: Map<string, SceneProductDetailResponse>,
): RowMaterial[] {
  if (!productId) return [];
  const detail = detailCache.get(productId);
  if (!detail) return [];
  const out: RowMaterial[] = [];
  for (const surface of detail.surfaces) {
    if (surface.locked) continue; // fixed finish, not user-choosable → omit
    // Pick wins; otherwise the surface's default finish.
    const materialId = picks[surface.surfaceKey] ?? surface.defaultMaterialId;
    if (!materialId) continue; // no pick and no default → nothing to show
    const material = surface.materials.find(m => m.id === materialId);
    if (!material) continue; // id references a material no longer on the surface
    out.push({ surfaceLabel: surface.surfaceLabel, materialName: material.name });
  }
  return out;
}
